import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

/**
 * useHistoryOverlay
 *
 * An overlay (a dialog, a sheet) that is open while the current history entry
 * says so. Opening pushes an entry for the same screen, so the phone's back
 * gesture (or the browser's back button) pops it: the overlay closes and the
 * screen stays. Closing it from inside (X, Escape) pops the same entry, so
 * both ways agree. Going somewhere from the overlay replaces its entry: back
 * from there returns to the screen it was opened on, not to the overlay.
 *
 * The entry carries this page load's mark. After a reload the old entry shows
 * its screen without the overlay: what was in it (a query) is gone anyway.
 */

const PAGE_LOAD = Math.random().toString(36).slice(2)

interface OverlayHistoryState { overlay?: string }

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

export interface HistoryOverlay {
  open: boolean
  /** Opens it (pushes its entry). Does nothing if it is already open. */
  show: () => void
  /** Closes it without going anywhere (pops its entry). */
  close: () => void
  /** Leaves for `to`, closing it (replaces its entry). To its own screen, it just closes. */
  go: (to: string) => void
}

export function useHistoryOverlay(name: string): HistoryOverlay {
  const navigate = useNavigate()
  const location = useLocation()
  const mark = `${name}:${PAGE_LOAD}`

  // The router applies history changes as a transition, a moment after the
  // tap; iOS only brings up the keyboard for a focus made during the tap. So
  // the overlay opens at once, on the entry it was opened from, until its own
  // entry arrives.
  const [openingFrom, setOpeningFrom] = useState<string | null>(null)
  if (openingFrom !== null && openingFrom !== location.key) setOpeningFrom(null)
  const open = (location.state as OverlayHistoryState | null)?.overlay === mark || openingFrom === location.key

  // The entry the last close popped. The browser answers back a moment later
  // (popstate); a second Escape or tap meanwhile must not go back twice.
  const popped = useRef<string | null>(null)
  useEffect(() => {
    popped.current = null
  }, [location.key])

  const show = useCallback(() => {
    if (open) return
    popped.current = null
    setOpeningFrom(location.key)
    // Same screen; whatever state the page keeps in its entry stays.
    const { pathname, search, hash, state } = location
    navigate({ pathname, search, hash }, { state: { ...(isRecord(state) ? state : {}), overlay: mark } })
  }, [open, location, navigate, mark])

  const close = useCallback(() => {
    if (!open || popped.current === location.key) return
    popped.current = location.key
    setOpeningFrom(null)
    navigate(-1)
  }, [open, location.key, navigate])

  const go = useCallback((to: string) => {
    if (open && to === location.pathname) close()
    else navigate(to, { replace: open })
  }, [open, location.pathname, close, navigate])

  return { open, show, close, go }
}
